import PropTypes from "prop-types";
import * as React from "react";
import { addonPropType } from "../../domain";
import { STATUS, uploadErrorCopy } from "../../domain/mediaUpload";
import {
  VIDEO_MESSAGE_STATE as S,
  videoMessageView,
} from "../../domain/videoMessage";
import useMediaUploadOutcome from "../../hooks/useMediaUploadOutcome";
import { retryMedia } from "../../services/mediaUpload";
import { useNotification } from "../../services/Notifications";
import {
  Box,
  Button,
  CheckCircleIcon,
  ErrorCircleIcon,
  PlayCircleIcon,
  RestartIcon,
  Spinner,
  Typography,
  VideocamIcon,
} from "../../themeV2";
import OrderAddonBadge, { withCancelled } from "../Addons/AddonBadge";
import VideoPlaybackDialog from "../MediaCapture/VideoPlaybackDialog";
import VideoMessageModal from "./VideoMessageModal";

const UPLOAD_TYPE = "customer_video_message";

const statusFor = ({ state, influencerName, outcome }) =>
  ({
    [S.PROCESSING]: { text: "Processing your video…" },
    [S.SENT]: { icon: <CheckCircleIcon />, text: "Sent", color: "success" },
    [S.LOCKED]: { icon: <CheckCircleIcon />, text: "Sent", color: "success" },
    [S.CLOSED]: { text: `Not sent — ${influencerName} has signed this item.` },
    [S.FAILED]: {
      icon: <ErrorCircleIcon />,
      text: uploadErrorCopy({ ...outcome, uploadType: UPLOAD_TYPE }),
      color: "error",
    },
  }[state] ?? null);

/* Mounted in every state so a change is announced: a live region that appears
   with its content is not read by most screen readers. */
const StatusLine = ({ status }) => (
  <Typography
    as="p"
    variant="body-sm"
    color={status?.color ?? "variant"}
    role="status"
  >
    {status && (
      <Box as="span" direction="row" gap={1} align="center">
        {status.icon && <span aria-hidden="true">{status.icon}</span>}
        {status.text}
      </Box>
    )}
  </Typography>
);

StatusLine.propTypes = {
  status: PropTypes.shape({
    icon: PropTypes.node,
    text: PropTypes.string.isRequired,
    color: PropTypes.string,
  }),
};

function VideoMessageRow({ addon, autographPurchase, onChange }) {
  const [dialog, setDialog] = React.useState(null);
  const notify = useNotification();

  const { autographPurchaseId, influencerName, videoMessageUrl } =
    autographPurchase;

  const { outcome, clear } = useMediaUploadOutcome({
    uploadType: UPLOAD_TYPE,
    referenceId: autographPurchaseId,
    onComplete: onChange,
  });

  React.useEffect(() => {
    if (outcome?.status !== STATUS.FAILED) return;

    onChange?.();
    notify({
      message: uploadErrorCopy({ ...outcome, uploadType: UPLOAD_TYPE }),
      type: "error",
      duration: 0,
      action:
        outcome.errorClassification === "transient"
          ? {
              label: "Try again",
              onClick: () => retryMedia(outcome.mediaUploadId).then(onChange),
            }
          : undefined,
    });
  }, [outcome, notify, onChange]);

  const { state, canWatch, canRecord } = videoMessageView({
    item: autographPurchase,
    outcome,
  });

  const openRecorder = () => {
    clear();
    setDialog("record");
  };

  const handleSent = React.useCallback(() => {
    setDialog(null);
    onChange?.();
  }, [onChange]);

  const closeDialog = () => setDialog(null);

  const recordLabel =
    {
      [S.FAILED]: "Record again",
      [S.SENT]: "Re-record",
    }[state] ?? "Record";

  const actions = (
    <Box direction="column" gap={2}>
      {canWatch && (
        <Button
          fullWidth
          size="sm"
          variant="tonal"
          leading={<PlayCircleIcon />}
          onClick={() => setDialog("watch")}
          aria-label="Watch video message"
        >
          Watch
        </Button>
      )}

      {canRecord && (
        <Button
          fullWidth
          size="sm"
          variant={state === S.SENT ? "text" : undefined}
          color={state === S.SENT ? undefined : "success"}
          leading={state === S.SENT ? <RestartIcon /> : <VideocamIcon />}
          onClick={openRecorder}
          aria-label={`${recordLabel} video message`}
        >
          {recordLabel}
        </Button>
      )}

      {state === S.PROCESSING && (
        <Button fullWidth size="sm" variant="tonal" disabled>
          <Spinner size="sm" aria-hidden="true" />
          Processing
        </Button>
      )}
    </Box>
  );

  const hasAction = canWatch || canRecord || state === S.PROCESSING;

  return (
    <React.Fragment>
      <OrderAddonBadge
        title={addon.title}
        description={addon.description}
        icon={addon.icon}
        additionalDetails={
          <StatusLine status={statusFor({ state, influencerName, outcome })} />
        }
        action={withCancelled(state === S.CANCELLED && !canWatch, () =>
          hasAction ? actions : null,
        )}
      />

      {dialog === "record" && (
        <VideoMessageModal
          autographPurchaseId={autographPurchaseId}
          influencerName={influencerName}
          isReplacing={Boolean(videoMessageUrl)}
          onSent={handleSent}
          onClose={closeDialog}
        />
      )}

      {dialog === "watch" && (
        <VideoPlaybackDialog
          src={videoMessageUrl}
          title="Your video message"
          description={`Sent to ${influencerName}`}
          videoLabel={`Your video message to ${influencerName}`}
          onClose={closeDialog}
        />
      )}
    </React.Fragment>
  );
}

VideoMessageRow.propTypes = {
  addon: addonPropType.isRequired,
  autographPurchase: PropTypes.shape({
    autographPurchaseId: PropTypes.number.isRequired,
    influencerName: PropTypes.string,
    videoMessageUrl: PropTypes.string,
    videoMessageProcessing: PropTypes.bool,
    signed: PropTypes.bool,
    cancelled: PropTypes.bool,
  }).isRequired,
  onChange: PropTypes.func,
};

export default VideoMessageRow;
